import React, { useState, useEffect } from 'react'
import {Helmet} from "react-helmet";
import { BrowserRouter as Router, Route, Redirect } from "react-router-dom"
import { createGlobalStyle, ThemeProvider } from 'styled-components'
import decoder from 'jwt-decode'
import { loadProfile, getStreamChatToken } from './api'
import { AuthContext } from './auth-context'
import { Provider } from 'react-redux'
import Login from './pages/login'
import SetPassword from './pages/set-password'
import Reset from './pages/reset'
import TeamupCallback from './pages/teamup-callback'
import Dashboard from './pages/dashboard'
import LoadingPage from './pages/loading'
import Terms from './pages/terms'
import SignUp from './pages/signup'
import store from './store'
import { AvatarTheme } from './themes/avatar-theme'
import { FBBCTheme } from './themes/fbbc-theme'
import favIcon from './images/theme/avatar/favicon.png'
import favIconFBBC from './images/theme/fbbc/favicon.ico'
import isPropValid from '@emotion/is-prop-valid';
import { StyleSheetManager } from 'styled-components';
import { SnackbarProvider } from 'notistack'

const themes = {
  "AVATAR": AvatarTheme,
  "FBBC": FBBCTheme
}

const getTitle = {
  "AVATAR": "Avatar Portal",
  "FBBC": "FBBC Portal",
}

const getIcon = {
  "AVATAR": favIcon,
  "FBBC": favIconFBBC,
}

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    border-radius: 0px;
  }

  body {
    color: #333;
    font-family: DM Sans, sans-serif;
    margin: 0;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  html {
    box-sizing: border-box;
  }

  *, *:before, *:after {
    box-sizing: inherit;
  }
`;

function App() {
  const [isAuth, updateAuth] = useState('')
  const [user, updateUser] = useState('')

  useEffect(() => {
    const jwt = window.localStorage.getItem('jwt')
    // if(Notification)
    //   Notification.requestPermission()

    if(jwt) {
      const userId = decoder(jwt).id
      getStreamChatToken(userId).then((res) => {
        window.localStorage.setItem('streamChatUser', res.data.id)
        window.localStorage.setItem('streamChatToken', res.data.token)
      }).catch((err) => {
        console.log(err)
      });
      loadProfile(userId).then((res) => {
        if (res.data.profile.active === 1) {
          updateUser(res.data.profile);
          updateAuth(true);
        } else {
          // Inactive account
          updateAuth(false)
        }
      }).catch((err) => {
        // Server Error
        updateAuth(false)
      })
    } else {
      // No token
      updateAuth(false)
    }
  }, [])

  const renderAuthDashboard = (isAuth) => {
    if(isAuth === '')
      return <LoadingPage />
    else if (isAuth)
      if (user.password_set === 1) {
        if (user.agree_to_terms === null) {
          return <Redirect to="/terms" />
        } else {
          return <Dashboard />
        }
      } else {
        return <Redirect to="/account-setup" />
      }
    else if (isAuth === false)
      return <Redirect to="/" />
  }

  const renderAuthPassword = (isAuth) => {
    if(isAuth === '')
      return <LoadingPage />
    else if (isAuth === true && user.password_set === 0)
      return <SetPassword updateAuth={updateAuth} updateUser={updateUser} />
      else if (isAuth === true && user.password_set === 1)
      return <Redirect to="/dashboard/overview" />
    else if (isAuth === false)
      return <Redirect to="/" />
  }

  const renderAuthTerms = (isAuth) => {
    if(isAuth === '')
      return <LoadingPage />
    else if (isAuth === true && (user.agree_to_terms !== null))
      return <Redirect to="/dashboard/overview" />
    else if (isAuth === true && (user.agree_to_terms === null))
      return <Terms />
    else if (isAuth === false)
      return <Redirect to="/" />
  }

  const renderAuthSignUp = (isAuth) => {
    if(isAuth === '')
      return <LoadingPage />
    else if (isAuth === true)
      return <Redirect to="/dashboard/overview" />
    else
      return <SignUp />
  }

  const logout = () => {
    window.localStorage.removeItem('jwt')
    window.localStorage.removeItem('streamChatUser')
    window.localStorage.removeItem('streamChatToken')
    updateAuth(false)
    updateUser('')

  }

  return (
    <>
      <Provider store={store}>
        <GlobalStyle />
        {/* Clears warnings caused by v5.10+ styled-components introducing transient props */}
        <StyleSheetManager shouldForwardProp={isPropValid}>
          <ThemeProvider theme={themes[process.env.REACT_APP_THEME]}>
            <AuthContext.Provider
              value={{
                isAuth: isAuth,
                logout: logout,
                user: user,
                updateUser: updateUser,
              }}
            >
              <SnackbarProvider 
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
              >
                <Helmet>
                  <title>{getTitle[process.env.REACT_APP_THEME]}</title>
                  <link rel="shortcut icon" href={getIcon[process.env.REACT_APP_THEME]} />
                </Helmet>
                <Router>
                  <Route exact path="/" render={() => <Login updateAuth={updateAuth} updateUser={updateUser} />} />
                  <Route exact path="/reset" render={() => <Reset />} />
                  <Route exact path="/teamup-callback" render={() => <TeamupCallback />} />
                  <Route exact path="/account-setup" render={() => renderAuthPassword(isAuth)} />
                  <Route exact path="/terms" render={() => renderAuthTerms(isAuth)} />
                  <Route exact path="/signup/:refid" render={() => renderAuthSignUp(isAuth)} />
                  <Route path="/dashboard" render={() => renderAuthDashboard(isAuth)} />
                </Router>
              </SnackbarProvider>
            </AuthContext.Provider>
          </ThemeProvider>
        </StyleSheetManager>
      </Provider>
    </>
  );
}

export default App;
